import React, { FC, useEffect, useState } from 'react';

import { useFieldArray, UseFieldArrayAppend, useForm, UseFormSetValue } from 'react-hook-form';

import {
  SHARE_EXCHANGE_INVESTOR_SELECTORS_FIELDS,
  CreateExternalShareExchangeWithCompanyFieldNames,
  SHARE_EXCHANGE_FORM_FIELDS,
} from 'constants/external-share-exchange';
import { UseModalReturnValues } from 'hooks/use-modal/useModal';
import { Deal, FormItem, Option } from 'interfaces';
import { ExternalShareExchangeWatchValues, FileUploadEntry } from 'interfaces/external-share-exchange.interface';
import {
  OverwrittenShareExchangeFormValues,
  OverwrittenShareExchangesValues,
} from 'interfaces/overwritten-share-exchange.interfaces';
import { selectCompanyShareClasses } from 'modules/company-iac/selectors';
import { getDealDetails, overrideDealData } from 'modules/deals/action';
import { getInvestors } from 'modules/investors/action';
import { selectInvestorsData } from 'modules/investors/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { AlertModal } from 'shared-components';
import { checkIsAdmin } from 'utils';

import ShareExchangeInvestorFields from '../share-exchanges/ShareExchangeInvestorFields';
import OverrideShareExchangeCommonFields from './OverrideShareExchangeCommonFields';

interface Props extends Omit<UseModalReturnValues, 'onOpen'> {
  selectedDeal: Deal;
  isActionLoading?: boolean;
}

const OverrideDealModal: FC<Props> = ({ selectedDeal, isActionLoading, ...modalProps }) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvestors, setIsLoadingInvestors] = useState(true);

  const shareClasses = useAppSelector(selectCompanyShareClasses);
  const [initialFileValues, setInitialFileValues] = useState<Partial<{ files: FileUploadEntry[] }>>({ files: [] });
  const [investorIdToSave, setNewInvestorIdToSave] = useState<number | null>(null);
  const investors = useAppSelector(selectInvestorsData)?.list || [];

  useEffect(() => {
    const fetchInvestors = async () => {
      try {
        await dispatch(
          getInvestors({
            page: 1,
            per_page: 20,
            query: selectedDeal.investorEmail,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error fetching investors:', error);
      }
      setIsLoadingInvestors(false);
    };

    fetchInvestors();
  }, [dispatch, selectedDeal.investorEmail]);

  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<OverwrittenShareExchangeFormValues>({
    defaultValues: {
      dealId: selectedDeal.id,
      investorId: investorIdToSave ?? selectedDeal.investorId,
      companyId: selectedDeal.companyId.toString(),
      investorFirstName: investors[0]?.firstName ?? '',
      investorLastName: investors[0]?.lastName ?? '',
      investorEmail: selectedDeal.investorEmail ?? '',
      files: [],
      email: {
        value: selectedDeal.investorEmail,
        label: selectedDeal.investorEmail,
      },
      closingDate: selectedDeal.closingDate,
      exchanges: selectedDeal.dealExchanges.map((exchange) => {
        return {
          classOfShares: exchange.classOfShares,
          noOfShares: exchange.noOfShares,
          companyPrice: exchange.companyPrice,
          closingDate: selectedDeal.closingDate,
          fundSharePrice: exchange.fundSharePrice,
          noExchangedShares: exchange.noExchangedShares,
          originalInvestmentPrice: exchange.originalInvestmentPrice ?? '',
        };
      }),
    },
  });

  const {
    fields: dealsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'exchanges',
  });

  const watchExchanges = watch('exchanges');
  const watchFiles = watch('files') as FileUploadEntry[];
  const watchInveastorFirstName = watch(
    CreateExternalShareExchangeWithCompanyFieldNames.FIRST_NAME as keyof OverwrittenShareExchangeFormValues,
  );
  const watchInveastorLastName = watch(
    CreateExternalShareExchangeWithCompanyFieldNames.LAST_NAME as keyof OverwrittenShareExchangeFormValues,
  );
  const watchEmail = watch('email');

  const readyToSaveFiles: FileUploadEntry[] = watchFiles.map((file) => ({
    ...file,
    file: new File([file.file], file.file.name),
    documentType:
      typeof file.documentType === 'object' && file.documentType !== null
        ? (file.documentType as Option).value
        : file.documentType ?? '',
  }));

  const getFields = (
    watchValues: ExternalShareExchangeWatchValues,
    classOfSharesOptions: Option[],
    dealIndex: number,
  ) => SHARE_EXCHANGE_FORM_FIELDS(dealIndex, setValue as unknown as any, classOfSharesOptions, watchValues);

  const reloadPage = () => {
    dispatch(getDealDetails({ id: selectedDeal.id, isAdmin: checkIsAdmin() }));
  };

  const handleOverrideDeal = () => {
    setIsLoading(true);
    dispatch(
      overrideDealData({
        dealId: selectedDeal.id,
        investorId: investorIdToSave || null,
        investorFirstName: (watchInveastorFirstName as string) ?? '',
        investorLastName: (watchInveastorLastName as string) ?? '',
        investorEmail: watchEmail.value,
        companyId: selectedDeal.companyId.toString(),
        closingDate: watchExchanges[0].closingDate.split('T')[0],
        exchanges: watchExchanges.map((exchange) => ({
          classOfShares: exchange.classOfShares,
          noOfShares: exchange.noOfShares,
          companyPrice: exchange.companyPrice,
          closingDate: exchange.closingDate.split('T')[0],
          fundSharePrice: exchange.fundSharePrice,
          noExchangedShares: exchange.noExchangedShares,
          originalInvestmentPrice: (exchange.originalInvestmentPrice || '').toString(),
        })),
        files: readyToSaveFiles,
      }),
    )
      .unwrap()
      .then(() => {
        reset();
      })
      .finally(() => {
        setIsLoading(false);
        modalProps.onClose();
        reloadPage();
      });
  };

  if (isLoadingInvestors) {
    return <div>Loading...</div>;
  }

  const handleInvestorId = (investorId: number | null) => {
    setNewInvestorIdToSave(investorId);
  };

  return (
    <AlertModal
      onSubmit={handleSubmit(handleOverrideDeal)}
      isLoading={isActionLoading || isLoading}
      title={'Please enter new data'}
      submitButtonText={'Save'}
      modalClassName='min-w-[70%] 2xl:min-w-[1048px]'
      {...modalProps}
    >
      <div className='flex flex-col w-full  pt-10 pb-2'>
        <form className='w-full mb-0 mt-4'>
          <ShareExchangeInvestorFields<OverwrittenShareExchangeFormValues>
            watch={watch}
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            formFields={
              SHARE_EXCHANGE_INVESTOR_SELECTORS_FIELDS as (
                emailOptions: Option[],
                setValue: UseFormSetValue<OverwrittenShareExchangeFormValues>,
                investors: any[],
                isFormEditable: boolean,
              ) => FormItem[]
            }
            fieldNames={CreateExternalShareExchangeWithCompanyFieldNames as unknown as any}
            hiddenEditButton={true}
            handleInvestorId={handleInvestorId}
          />
          <OverrideShareExchangeCommonFields
            shareClasses={shareClasses}
            getFields={getFields}
            dealsFields={dealsFields}
            register={register}
            watch={watch}
            control={control}
            errors={errors}
            setValue={setValue}
            companyName={''}
            reset={reset}
            shareExchanges={watchExchanges}
            remove={remove}
            append={append as unknown as UseFieldArrayAppend<OverwrittenShareExchangesValues, 'exchanges'>}
            initialFileValues={initialFileValues}
          />
        </form>
      </div>
    </AlertModal>
  );
};

export default OverrideDealModal;
